import { Component, type FormEvent, type SyntheticEvent } from "react";
import { pick } from "ramda";
import { observer } from "mobx-react";
import { Stack, TextField, DefaultButton, Checkbox, Label } from "@fluentui/react";
import { action, computed, makeObservable } from "mobx";
import { type SafetyMessageUpdate, type SafetyMessage } from "../../api";
import { doubleBindString } from "../../utils/double-bind";
import { createUuid } from "../../utils/uuid";
import { type FormUpdateProps } from "../../utils/form-update-props";
import { PrimaryButtonValidation } from "../atoms/primary-button-validation";
import { ElofleetDialogFooter } from "../atoms/elofleet-dialog-footer";
import sizes from "../sizes.scss";

export interface FormUpdateSafetyMessageProps extends FormUpdateProps<SafetyMessage> {}

import { store } from "../../store";

@observer
export class FormUpdateSafetyMessage extends Component<FormUpdateSafetyMessageProps> {
    private validationId = createUuid();

    constructor(props: FormUpdateSafetyMessageProps) {
        super(props);
        makeObservable(this);
    }

    private triggerValidation(): void {
        store.repositorySafetyMessages.validation.update.updateModel(
            this.validationId,
            this.safetyMessageUpdate,
        );
    }

    componentDidMount(): void {
        store.repositorySafetyMessages.validation.update.initializeModel(
            this.validationId,
            this.safetyMessageUpdate,
            this.props.id,
        );
    }

    public componentWillUnmount(): void {
        store.repositorySafetyMessages.discardMutableCopy(
            FormUpdateSafetyMessage.name,
            this.props.id,
        );
    }

    @computed private get safetyMessage(): SafetyMessage | undefined {
        return store.repositorySafetyMessages.mutableCopyById(
            FormUpdateSafetyMessage.name,
            this.props.id,
        );
    }

    @computed private get safetyMessageUpdate(): SafetyMessageUpdate {
        return pick(["body", "active"], this.safetyMessage);
    }

    @action.bound
    private async updateSafetyMessage(evt: SyntheticEvent<HTMLFormElement>): Promise<void> {
        evt.preventDefault();
        const safetyMessage = await store.repositorySafetyMessages.update(
            this.props.id,
            this.safetyMessageUpdate,
        );
        this.props.onUpdate(safetyMessage);
    }

    @action.bound private changeActive(
        _event?: FormEvent<HTMLElement | HTMLInputElement>,
        checked?: boolean,
    ): void {
        this.safetyMessage!.active = checked!;
        this.triggerValidation();
    }

    public render(): JSX.Element {
        const primaryButton = (
            <PrimaryButtonValidation
                text={store.i18n.t("formUpdateSafetyMessage.submit.text")}
                validation={store.repositorySafetyMessages.validation.update}
                validationId={this.validationId}
            />
        );
        return (
            <form onSubmit={this.updateSafetyMessage}>
                <Stack
                    horizontal
                    verticalAlign="center"
                    styles={{
                        root: {
                            minWidth: "600px",
                        },
                    }}
                >
                    <Stack.Item tokens={{ padding: `0px ${sizes.formPaddingHorizontal}` }}>
                        <TextField
                            label={store.i18n.t("formUpdateSafetyMessage.body.label")}
                            disabled={!this.safetyMessage}
                            {...doubleBindString(this.safetyMessage!, "body", () =>
                                this.triggerValidation(),
                            )}
                            required
                            errorMessage={store.i18n.formatFieldValidationState(
                                store.repositorySafetyMessages.validation.update.getFieldValidationState(
                                    this.validationId,
                                    "body",
                                ),
                            )}
                            styles={{
                                root: {
                                    minWidth: "450px",
                                },
                            }}
                        />
                    </Stack.Item>
                    <Stack horizontalAlign="center">
                        <Label>{store.i18n.t("formUpdateSafetyMessage.active.label")}</Label>
                        <Checkbox
                            defaultChecked={this.safetyMessage?.active}
                            onChange={this.changeActive}
                        />
                    </Stack>
                </Stack>
                {this.props.asDialogContent ? (
                    <ElofleetDialogFooter>
                        <DefaultButton
                            text={store.i18n.t("formUpdateSafetyMessage.cancel.text")}
                            onClick={this.props.onDialogCancel}
                        />
                        {primaryButton}
                    </ElofleetDialogFooter>
                ) : (
                    <Stack horizontal horizontalAlign="end">
                        {primaryButton}
                    </Stack>
                )}
            </form>
        );
    }
}
